import { OnInit, ElementRef, HostListener } from '@angular/core';
import { Component } from '@angular/core';
import { Dialog } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { DividerModule } from 'primeng/divider';
import { CommonModule } from '@angular/common';
import { TooltipModule } from 'primeng/tooltip';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AuthService } from '@/services/auth.service';
import { Router } from '@angular/router';
import { ApiService } from '@/services/api.service';
import { ActivatedRoute } from '@angular/router';
import { User, SessionData } from '@/helper/auth.helper';
import { DialogService } from 'primeng/dynamicdialog';
import { ImpersonateUserDialogComponent } from '@/modules/management/pages/impersonate-user/impersonate-user-dialog.component';
import { ToastService } from '@/services/toast.service';
import { markLogoutInProgress } from '@/helper/logout-marker.helper';

@Component({
    selector: 'app-user-menu-dropdown',
    standalone: true,
    imports: [CommonModule, Dialog, ButtonModule, DividerModule, TooltipModule, TranslatePipe],
    templateUrl: './user-menu-dropdown.html',
    styleUrl: './user-menu-dropdown.scss',
    providers: [DialogService]
})
export class UserMenuDropdown implements OnInit {
    visible: boolean = false;
    apiUrl: string = (window as any)['__env'].apiEndpoint;
    user: Partial<User> = new User();
    sessionData: SessionData | undefined;
    roles: string[] = [];

    position: 'left' | 'right' | 'top' | 'bottom' | 'center' | 'topleft' | 'topright' | 'bottomleft' | 'bottomright' = 'topright';

    showSpinner = false;

    constructor(
        public authService: AuthService,
        public apiService: ApiService,
        public router: Router,
        private elementRef: ElementRef,
        private route: ActivatedRoute,
        private dialogService: DialogService,
        private toastService: ToastService,
        private translateService: TranslateService
    ) { }

    ngOnInit() {
        this.authService.sessionData.subscribe(data => {
            this.user = data.user;
            this.sessionData = data;
        });
    }

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent) {
        if (!this.visible) return;

        const clickedElement = event.target as HTMLElement;
        const dialogElement = document.querySelector('.p-dialog');
        const triggerButton = this.elementRef.nativeElement.querySelector('.layout-topbar-action');

        // Check if click is outside both the dialog and the trigger button
        if (
            dialogElement &&
            !dialogElement.contains(clickedElement) &&
            !triggerButton?.contains(clickedElement)
        ) {
            this.visible = false;
        }
    }

    showDialog(position: 'left' | 'right' | 'top' | 'bottom' | 'center' | 'topleft' | 'topright' | 'bottomleft' | 'bottomright') {
        if (!this.visible) {
            this.position = position;
            this.visible = true;
        } else {
            this.visible = false;
        }
    }

    goToProfile() {
        if (this.user) {
            this.visible = false;
            if (this.user.isSystemAdmin || (this.user.roles && this.user.roles.length && this.user.roles.includes("administration"))) {
                this.router.navigate(['/admin/profile']);
            }
        }
    }

    goToProfileEdit() {
        if (this.user) {
            if (this.user.roles && this.user.roles.length && (this.user.isSystemAdmin || this.user.roles.includes("administration"))) {
                this.visible = false;
                this.router.navigate(['/admin/profile/edit']);
            }
        }
    }

    goToAdminPage() {
        if (this.user) {
            if (this.user.isSystemAdmin || (this.user.roles && this.user.roles.length && this.user.roles.includes("administration"))) {
                this.visible = false;
                this.router.navigate(['/admin']);
            }
        }
    }

    goToHomePage() {
        if (this.user) {
            if (this.user.isSystemAdmin || (this.user.roles && this.user.roles.length && this.user.roles.includes("administration"))) {
                this.visible = false;
                this.router.navigate(['/home']);
            }
        }
    }


    goToDeliveryAddresses() {
        this.visible = false;
        this.router.navigate(['/store/profile/myaddresses']);
        this.visible = false;
    }

    openImpersonateDialog() {
        this.visible = false;
        const ref = this.dialogService.open(ImpersonateUserDialogComponent, {
            width: '500px',
            modal: true,
            header: this.translateService.instant('impersonate.title'),
            closable: true
        });
    }

    async endImpersonation(): Promise<void> {
        this.visible = false;
        try {
            await this.authService.endImpersonation();
            await this.toastService.showSuccessToast(
                this.translateService.instant('generics.success'),
                this.translateService.instant('impersonate.ended')
            );
            this.router.navigate(['/admin']);
        } catch (error) {
            console.error('Error ending impersonation:', error);
            await this.toastService.showErrorToast(
                this.translateService.instant('generics.error'),
                this.translateService.instant('impersonate.failed')
            );
        }
    }

    logout() {
        markLogoutInProgress();

        if (this.user.oidcIdentifier?.length) {
            this.authService.oidcLogout();
        } else {
            this.authService.logout().subscribe((success) => {
                if (success) {
                    this.router.navigate(['/login']);
                }
            });
        }
    }
}
